/**
 * SA-1387 (spec §5.2, §5.3): the Mailboxes page's pure rules — what a list
 * row says, how the list groups, which pane the address names and how the
 * page writes it. No DOM, no queries.
 */
import type { Mailbox, MailboxChannel, MailboxProvider, ProviderClientConfig } from '@/services/mailboxes'
import { backloadLine, backloadWords } from './backloadText'
import { DIFFERENT_ACCOUNT, accountLine } from './connectedAccountText'
import { isGroupSource } from './groupSourceText'
import { healthLine } from './healthText'
import {
  BINDING_WARNING_FALLBACK, BINDING_WARNING_LABEL, GROUP_HEADING, parkedText, postCommitText, waitingWords,
} from './mailboxesText'

export type MailboxTab = 'overview' | 'routing' | 'triage'

const TABS: readonly MailboxTab[] = ['overview', 'routing', 'triage']

export interface AttentionLine {
  text: string
  tone: 'destructive' | 'warning' | 'muted'
}

/** The first of spec §5.2's ten items that holds; `setup` is item 10. */
export function attentionLine(mb: Mailbox, now: Date, setup: string): AttentionLine {
  const health = healthLine(mb, now)
  if (health) return { text: health, tone: 'destructive' }
  if (mb.status === 'reauth_required') return { text: 'Reauth needed', tone: 'destructive' }
  if (mb.binding_warning) {
    return { text: BINDING_WARNING_LABEL[mb.binding_warning] ?? BINDING_WARNING_FALLBACK, tone: 'destructive' }
  }
  if (!isGroupSource(mb) && accountLine(mb).mismatch) {
    return { text: DIFFERENT_ACCOUNT, tone: 'warning' }
  }
  if (mb.send_reconsent_needed) return { text: 'Re-consent needed for outbound', tone: 'warning' }
  if ((mb.parked_count ?? 0) > 0) return { text: parkedText(mb.parked_count as number), tone: 'warning' }
  if ((mb.post_commit_failure_count ?? 0) > 0) {
    return { text: postCommitText(mb.post_commit_failure_count as number), tone: 'warning' }
  }
  const waiting = waitingWords(mb)
  if (waiting.length > 0) return { text: waiting.join(' · '), tone: 'muted' }
  if (mb.backload?.status === 'loading') {
    return { text: backloadLine(mb.backload, mb.status, backloadWords(mb.channel)), tone: 'muted' }
  }
  return { text: setup, tone: 'muted' }
}

export interface MailboxGroup {
  channel: MailboxChannel
  heading: string | null
  mailboxes: Mailbox[]
}

export function groupMailboxes(mailboxes: readonly Mailbox[]): MailboxGroup[] {
  const channels: MailboxChannel[] = ['email', 'whatsapp-group']
  const groups = channels
    .map((channel) => ({
      channel,
      heading: null as string | null,
      mailboxes: mailboxes.filter((m) => (m.channel ?? 'email') === channel),
    }))
    .filter((g) => g.mailboxes.length > 0)
  if (groups.length > 1) {
    for (const g of groups) g.heading = GROUP_HEADING[g.channel]
  }
  return groups
}

export type Pane =
  | { kind: 'new' }
  | { kind: 'providers' }
  | { kind: 'mailbox'; mailbox: Mailbox; tab: MailboxTab }
  | { kind: 'empty' }
  | { kind: 'loading' }

export type PaneTarget =
  | { kind: 'new' }
  | { kind: 'providers' }
  | { kind: 'mailbox'; id: string; tab?: MailboxTab }
  | { kind: 'none' }

/** Spec §5.3: which pane the address names, with its fallbacks. */
export function paneFrom(
  params: URLSearchParams,
  mailboxes: readonly Mailbox[] | undefined,
  providersOffered: boolean | null,
): Pane {
  const pane = params.get('pane')
  if (pane === 'new') return { kind: 'new' }
  if (pane === 'providers' && providersOffered !== false) return { kind: 'providers' }
  if (mailboxes === undefined) return { kind: 'loading' }
  if (mailboxes.length === 0) return { kind: 'empty' }
  const named = mailboxes.find((m) => m.id === params.get('mailbox'))
  const mailbox = named ?? mailboxes[0]
  // An unknown id falls back to the first mailbox on Overview; the asked tab was for another one.
  const asked = named ? params.get('tab') : null
  const tab = TABS.find((t) => t === asked) ?? 'overview'
  return {
    kind: 'mailbox',
    mailbox,
    tab: tab === 'triage' && mailbox.sink_type !== 'router' ? 'overview' : tab,
  }
}

/** A new set of parameters naming the target; every key the page does not own is kept. */
export function withPane(params: URLSearchParams, target: PaneTarget): URLSearchParams {
  const next = new URLSearchParams(params)
  next.delete('pane')
  next.delete('mailbox')
  next.delete('tab')
  if (target.kind === 'new') next.set('pane', 'new')
  else if (target.kind === 'providers') next.set('pane', 'providers')
  else if (target.kind === 'mailbox') {
    next.set('mailbox', target.id)
    if (target.tab && target.tab !== 'overview') next.set('tab', target.tab)
  }
  return next
}

// SA-1422: a refusal also carries `basis` and `other`. Its `mailbox` is the pane key, never dropped.
const LANDING_KEYS = ['oauth_error', 'expected', 'account', 'basis', 'other', 'connected'] as const

/** The OAuth callback's landing parameters, read once; null when none is present. */
export function landingParams(params: URLSearchParams): {
  next: URLSearchParams
  oauthError: string | null
  expected: string | null
  account: string | null
  basis: string | null
  other: string | null
} | null {
  if (!LANDING_KEYS.some((k) => params.has(k))) return null
  const connected = params.get('connected')
  let next = new URLSearchParams(params)
  for (const k of LANDING_KEYS) next.delete(k)
  if (connected !== null) next = withPane(next, { kind: 'mailbox', id: connected })
  return {
    next,
    oauthError: params.get('oauth_error'),
    expected: params.get('expected'),
    account: params.get('account'),
    basis: params.get('basis'),
    other: params.get('other'),
  }
}

/** True when a provider with an OAuth client has a loaded config with no client id. */
export function providerNeedsClient(
  providers: readonly MailboxProvider[],
  configs: Record<string, ProviderClientConfig>,
): boolean {
  return providers.some((p) => {
    if (p.oauth_config_id === null) return false
    const cfg = configs[p.oauth_config_id]
    return cfg !== undefined && !cfg.oauth_client_id?.trim()
  })
}
